// The course: lessons in order, and the word order they release.
//
// The MDX lessons in src/lessons/ (their `meta`) are the course: every lesson of the 50 is
// written, and each meta is its week's one source of truth (docs/COURSE.md "Lesson hub"). The
// Duolingo units of /data/course-order.json only label a lesson ("Duolingo: Lunar class, Solar
// class"): a lesson names its units in `duolingoUnit` (one id or a list), there is no mapping by
// order (pure function `buildCatalog`, tested in test/lessons.test.ts).
//
// The course word list is the merged lessons' lemmaIds in lesson order (first occurrence wins).
// The server's D1 `lesson`/`lesson_item` tables must hold the same lessons (same ids and orders)
// for /api/sync to accept lesson progress and for its frontier to count the same words.
import type { LessonMeta } from '@valyrio/engine/types';
import { lessonMetas, type LessonFileMeta } from '../lessons';
import { courseWordList, lessonsOpen, passedByAll, passedThrough, unlockDateOf, type CourseWord } from './engine';
import type { LessonProgressRow, SharedState } from '@valyrio/engine/api';
import type { PaceConfig } from '@valyrio/engine/types';

export interface CourseUnit {
  id: string;
  order: number;
  title: string;
  source?: string;
  topics?: string[];
  lemmaIds: string[];
}

export interface CourseOrderFile {
  version: string;
  generatedAt: string;
  rules?: string[];
  units: CourseUnit[];
  wordList: string[];
  frequency?: Record<string, number>;
}

export function isCourseOrder(x: unknown): x is CourseOrderFile {
  const c = x as CourseOrderFile | null;
  return !!c && Array.isArray(c.units) && Array.isArray(c.wordList);
}

/** One lesson of the course (an MDX file). */
export interface CatalogLesson {
  meta: LessonFileMeta;
  /** Always true since every lesson is written (kept for the lesson states). */
  written: boolean;
  /** The first Duolingo unit this lesson follows (course-order.json), if any. */
  unit?: CourseUnit;
  /** Every Duolingo unit it covers (a merged lesson covers two): the "Duolingo: X" label. */
  units?: CourseUnit[];
}

/** The lessons in order, each with the Duolingo units its meta names (for the label only). */
export function buildCatalog(metas: readonly LessonFileMeta[], units: readonly CourseUnit[] | null): CatalogLesson[] {
  const unitById = new Map((units ?? []).map((u) => [u.id, u]));
  return [...metas]
    .sort((a, b) => a.order - b.order)
    .map((m) => {
      const ids = m.duolingoUnits ?? (m.duolingoUnit ? [m.duolingoUnit] : []);
      const us = ids.map((id) => unitById.get(id)).filter((u): u is CourseUnit => !!u);
      return { written: true, meta: m, ...(us[0] ? { unit: us[0], units: us } : {}) };
    });
}

/** Units as LessonMeta in their own order (kept for callers that predate the MDX lessons). */
export function unitsAsLessons(c: CourseOrderFile): LessonMeta[] {
  return c.units.map((u) => ({ id: u.id, order: u.order, titleFi: u.title, titleEn: u.title, lemmaIds: u.lemmaIds, exampleIds: [] }));
}

// The last course-order units seen (set by courseWords, which actions.ts calls on start-up and
// when a newer course-order.json arrives). The lessons page reads the catalogue through here.
let lastUnits: CourseUnit[] | null = null;

/**
 * The current catalogue: the written lessons + the last seen course-order units.
 * In a component, pass the store's `course` (`currentCatalog(course)`): the units arrive with it,
 * and the React Compiler memoizes a call on its arguments, so a call without one would keep the
 * catalogue from before the course arrived.
 */
export function currentCatalog(_course?: unknown): CatalogLesson[] {
  return buildCatalog(lessonMetas, lastUnits);
}

/** Course word list from a catalogue (release order). */
export function catalogWords(catalog: readonly CatalogLesson[]): CourseWord[] {
  return courseWordList(catalog.map((l) => l.meta));
}

export function courseWords(c: CourseOrderFile): CourseWord[] {
  lastUnits = c.units;
  return catalogWords(buildCatalog(lessonMetas, c.units));
}

// ---------- course plan (/data/course-plan.json, see ARCHITECTURE.md "Course plan") ----------

/** One planned lesson: LessonMeta fields + the words it releases (own + vocabulary quota). */
export interface PlanLesson extends LessonMeta {
  unlockDate?: string;
  release?: number;
  ownLemmaIds?: string[];
  quotaLemmaIds?: string[];
}

export interface CoursePlanFile {
  version: string;
  generatedAt: string;
  courseOrderVersion?: string;
  lessons: PlanLesson[];
  unallocated?: string[];
  missingOrders?: number[];
}

export function isCoursePlan(x: unknown): x is CoursePlanFile {
  const c = x as CoursePlanFile | null;
  return !!c && typeof c.version === 'string' && Array.isArray(c.lessons) && c.lessons.every((l) => l && typeof l.id === 'string' && Array.isArray(l.lemmaIds));
}

// Set when a plan is loaded (actions.ts). With a plan, the course word list is the plan's and
// lessons without an MDX file contribute no words (their unit words sit in earlier quotas).
let planLoaded = false;

/** Course word list from the plan: `courseWordList(plan.lessons)` (release order, D1's order). */
export function planWords(plan: CoursePlanFile): CourseWord[] {
  planLoaded = true;
  return courseWordList(plan.lessons);
}

export function hasCoursePlan(): boolean {
  return planLoaded;
}

/** Tests only. */
export function resetCoursePlan(): void {
  planLoaded = false;
}

export async function fetchCoursePlan(): Promise<CoursePlanFile | null> {
  try {
    const res = await fetch('/data/course-plan.json', { cache: 'no-cache' });
    if (!res.ok || !(res.headers.get('content-type') ?? '').includes('json')) return null;
    const j: unknown = await res.json();
    return isCoursePlan(j) ? j : null;
  } catch {
    return null;
  }
}

// ---------- lesson state ----------

export type LessonState = 'locked' | 'unwritten' | 'open' | 'passed';

/**
 * - locked: not open for both yet — its Saturday has not come, or one of you has not passed the
 *   lesson before it (engine lessonsOpen); visible, never openable — no peeking ahead;
 * - unwritten: (no longer happens: every lesson is written) a lesson without its file;
 * - open: open and written, check not passed yet;
 * - passed: you passed its check (≥ 80 %; hasPassed); can be replayed any time.
 * `through`: lessons 1…k the shared state counts as yours (SharedState.lessonsPassed[me]).
 */
export function lessonState(l: CatalogLesson, lessonsUnlocked: number, progress: readonly LessonProgressRow[], through = 0): LessonState {
  if (l.meta.order > lessonsUnlocked) return 'locked';
  if (!l.written) return 'unwritten';
  return hasPassed(l.meta, progress, through) ? 'passed' : 'open';
}

/**
 * The one "you passed this lesson" rule (layout v2 fix batch, 2026-10-06): your check of it passed
 * on this device or synced (`progress`), or the shared state counts lessons 1…`through` as yours.
 * The week road's WeekRow.mine, lessonState and "Harjoittele viikkoa N" all ask this; "passed" on
 * the road (WeekRow.kind) is something else — behind the frontier, passed by both of you.
 */
export function hasPassed(lesson: Pick<LessonMeta, 'id' | 'order'>, progress: readonly LessonProgressRow[], through = 0): boolean {
  return lesson.order <= through || progress.some((p) => p.lessonId === lesson.id && !!p.passedAt);
}

/** Local date ("YYYY-MM-DD", a Saturday) on which a lesson is scheduled to open for both. */
export function unlockDate(order: number, pace: PaceConfig): string {
  return unlockDateOf(order, pace);
}

/** Lessons 1…k I have passed (lesson progress on this device, synced or not). */
export function myPassedThrough(progress: readonly LessonProgressRow[], metas: readonly Pick<LessonFileMeta, 'id' | 'order'>[] = lessonMetas): number {
  const orderOf = new Map(metas.map((m) => [m.id, m.order]));
  return passedThrough(progress.filter((p) => p.passedAt).map((p) => orderOf.get(p.lessonId) ?? 0));
}

/**
 * The shared state with my own passes on this device folded in: a pass not synced yet opens the
 * next lesson at once when the other has passed already (and its Saturday has come). The server
 * stays the authority — this only ever adds what the server could not know yet. A state without
 * `lessonsPassed` (an older server) is left as it is; the device's own estimate (never synced,
 * lib/shared.ts estimateShared) has an empty `lessonsPassed`: only my passes are known then.
 */
export function withLocalPasses(shared: SharedState, o: { meId: string; mine: number; pace: PaceConfig; now: Date; lessonCount: number }): SharedState {
  if (o.lessonCount <= 0 || !shared.lessonsPassed) return shared;
  const passed = { ...shared.lessonsPassed };
  passed[o.meId] = Math.max(passed[o.meId] ?? 0, o.mine);
  const open = lessonsOpen(o.now, o.pace, passedByAll(passed), o.lessonCount);
  const unlocked = Math.max(shared.lessonsUnlocked, open);
  return {
    ...shared,
    lessonsUnlocked: unlocked,
    nextUnlock: unlockDateOf(unlocked + 1, o.pace),
    lessonsPassed: passed,
    lessonCount: shared.lessonCount || o.lessonCount,
  };
}

/**
 * Lesson ids whose released words this user may study as new cards:
 * every passed lesson, plus every lesson that has no written check yet (a Duolingo unit standing
 * in): its words cannot wait for a check that does not exist. Returns null when no lesson is
 * written at all — then every released word is available (the pre-lessons behaviour).
 */
export function openLessonIds(catalog: readonly CatalogLesson[], progress: readonly LessonProgressRow[], withPlan = hasCoursePlan()): string[] | null {
  if (!catalog.some((l) => l.written)) return null;
  const passed = new Set(progress.filter((p) => p.passedAt).map((p) => p.lessonId));
  // With a course plan every planned lesson is written and has a check; unwritten stand-ins
  // release nothing, so only passed lessons open their words.
  if (withPlan) return [...passed];
  return catalog.filter((l) => !l.written || passed.has(l.meta.id)).map((l) => l.meta.id);
}

/** Where the two of you are against the calendar, for the frontier strip's one line. */
export interface CourseLine {
  /** Lessons behind schedule (0 = on schedule). */
  behind: number;
  allOpen: boolean;
  /** The next lesson's date has come: it waits for these to pass the current lesson. */
  waitsFor: null | 'both' | 'me' | 'other';
}

export function courseLine(
  shared: SharedState,
  o: { meId: string; partnerId?: string | null; today: string; pace: PaceConfig; lessonCount?: number },
): CourseLine {
  const count = shared.lessonCount || o.lessonCount || 0;
  const open = shared.lessonsUnlocked;
  const scheduled = Math.min(shared.lessonsScheduled ?? open, count || Infinity);
  const allOpen = count > 0 && open >= count;
  let waitsFor: CourseLine['waitsFor'] = null;
  if (!allOpen && open > 0 && unlockDateOf(open + 1, o.pace) <= o.today) {
    const passed = shared.lessonsPassed ?? {};
    const me = (passed[o.meId] ?? 0) < open;
    const other = !!o.partnerId && (passed[o.partnerId] ?? 0) < open;
    waitsFor = me && other ? 'both' : me ? 'me' : other ? 'other' : null;
  }
  return { behind: Math.max(0, scheduled - open), allOpen, waitsFor };
}

/** Pass mark of a lesson check. */
export const PASS_SCORE = 0.8;

export async function fetchCourseOrder(): Promise<CourseOrderFile | null> {
  try {
    const res = await fetch('/data/course-order.json', { cache: 'no-cache' });
    if (!res.ok || !(res.headers.get('content-type') ?? '').includes('json')) return null;
    const j: unknown = await res.json();
    return isCourseOrder(j) ? j : null;
  } catch {
    return null;
  }
}
